import { useData, computeStats } from '../hooks/useData';

function StatCard({ icon, label, value, sub, color, ring }) {
  return (
    <div style={{
      background: 'var(--bg-3)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: '14px 16px',
      transition: 'all 0.2s var(--ease)',
      position: 'relative',
      overflow: 'hidden',
    }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--border-2)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Subtle glow for accent cards */}
      {color === 'var(--accent)' && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          width: 60, height: 60,
          background: 'radial-gradient(circle, rgba(0,255,136,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        {ring !== undefined && (
          <div style={{ position: 'relative', width: 30, height: 30 }}>
            <svg width="30" height="30" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="15" cy="15" r="11" fill="none" stroke="var(--border-2)" strokeWidth="2.5" />
              <circle cx="15" cy="15" r="11" fill="none" stroke={color || 'var(--accent)'}
                strokeWidth="2.5"
                strokeDasharray={`${2 * Math.PI * 11}`}
                strokeDashoffset={`${2 * Math.PI * 11 * (1 - ring / 100)}`}
                strokeLinecap="round"
                style={{ filter: color === 'var(--accent)' ? 'drop-shadow(0 0 3px rgba(0,255,136,0.5))' : 'none' }}
              />
            </svg>
            <span style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
              color: color || 'var(--accent)',
            }}>
              {ring}%
            </span>
          </div>
        )}
      </div>

      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: 24,
        fontWeight: 700,
        color: color || 'var(--accent)',
        lineHeight: 1,
        marginBottom: 5,
        letterSpacing: '-0.02em',
        textShadow: color === 'var(--accent)' ? '0 0 20px rgba(0,255,136,0.3)' : 'none',
      }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: sub ? 2 : 0 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>{sub}</div>}
    </div>
  );
}

export default function RightPanel({ refreshTrigger }) {
  const { meetings, actions, loading } = useData(refreshTrigger);
  const stats = computeStats(meetings, actions);

  const fmtMins = (m) => {
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem ? `${h}h ${rem}m` : `${h}h`;
  };

  const streakColor = stats.streak >= 5 ? 'var(--accent)' : stats.streak >= 2 ? 'var(--yellow)' : 'var(--text-3)';
  const rateColor = stats.completionRate >= 75 ? 'var(--accent)' : stats.completionRate >= 40 ? 'var(--yellow)' : 'var(--red)';

  const STATUS_COLOR = {
    done: 'var(--accent)', error: 'var(--red)',
    transcribing: 'var(--blue)', analysing: 'var(--yellow)', pending: 'var(--text-3)'
  };

  return (
    <div style={{
      width: 220,
      flexShrink: 0,
      background: 'var(--bg-2)',
      borderLeft: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px 12px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-3)' }}>
          Your Stats
        </span>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: 'var(--accent)',
          boxShadow: '0 0 6px var(--accent)',
          display: 'inline-block',
        }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} className="skeleton" style={{ height: 84, borderRadius: 12 }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <StatCard icon="🎙️" label="Analysed" value={fmtMins(stats.minutesAnalysed)} sub="of meeting audio" color="var(--accent)" />
            <StatCard icon="⚡" label="Time Saved" value={fmtMins(stats.minutesSaved)} sub="vs manual notes" color="var(--blue)" />
            <StatCard icon="✅" label="Completion" value={`${stats.completionRate}%`} sub={`${stats.doneActs}/${stats.totalActs} tasks`} color={rateColor} ring={stats.completionRate} />
            <StatCard icon="📅" label="Most Active" value={stats.mostActiveDay === '—' ? '—' : stats.mostActiveDay.slice(0, 3)} sub={stats.mostActiveDay === '—' ? 'no meetings yet' : `${stats.mostActiveDay}s`} color="var(--purple)" />
            <StatCard icon={stats.streak >= 3 ? '🔥' : '📆'} label="Day Streak" value={stats.streak} sub={stats.streak === 0 ? 'start today!' : `${stats.streak} day${stats.streak !== 1 ? 's' : ''} straight`} color={streakColor} />
          </div>
        )}

        {!loading && stats.totalMeetings > 0 && (
          <>
            <div style={{ height: 1, background: 'var(--border)', margin: '12px 0' }} />
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-3)', marginBottom: 8 }}>
              Recent
            </div>
            {meetings.slice(0, 5).map(m => {
              const timeAgo = () => {
                const diff = Date.now() - new Date(m.created_at).getTime();
                const mins = Math.floor(diff / 60000);
                if (mins < 60) return `${mins}m ago`;
                const hrs = Math.floor(mins / 60);
                if (hrs < 24) return `${hrs}h ago`;
                return `${Math.floor(hrs / 24)}d ago`;
              };
              return (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border)',
                  transition: 'opacity 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <span style={{ color: STATUS_COLOR[m.status], fontSize: 7, marginTop: 4, flexShrink: 0 }}>●</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text)' }}>{m.title}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--mono)', marginTop: 2 }}>{timeAgo()}</div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {!loading && stats.totalMeetings === 0 && (
          <div style={{ marginTop: 12, padding: 16, background: 'var(--bg-3)', borderRadius: 12, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🚀</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.6 }}>
              Analyse your first meeting to see stats
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
